import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, ComputedRef, watch } from "vue";
import { useRoute, useRouter } from "vue-router/composables";
import { Category, Item, StorageKey } from "@/types/components/V3SideBar.type";
import V3SideBarGroup from "@/components/v3-side-bar/V3SideBarGroup.vue";
import V3SideBarItemNew from "@/components/v3-side-bar/V3SideBarItemNew.vue";
import V3SideBarItemExpandableNew from "@/components/v3-side-bar/V3SideBarItemExpandableNew.vue";
import { isItemActive } from "@/components/v3-side-bar/v3-side-bar-util";
import { sideBarStore } from "@/store/v3-side-bar";
import { FavoriteSearch } from "@/models/search/FavoriteSearch";
import store from "@/store";
import { AnalysisType } from "@/const/user-trend";
import { LoginUser } from "@/models/auth/UgUser";
import { Client } from "@/models/Client";
import { useI18n } from "@/composables/useI18n";
interface RouteUrl {
  routeName?: string;
  url?: string;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'V3SideBarNew',
  setup(__props) {

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const {
  isSideBarMinimized,
  isAnalysisExpanded,
  isPinExpanded,
  setIsAnalysisExpanded,
  setIsPinExpanded
} = sideBarStore();

const isHovered = ref(false);
const isFavoriteSearchBalloonShown: ComputedRef<boolean> = computed(
  () => store.state.preference.favoriteSearchBalloonPositionY !== ""
);
const favoriteSearches: ComputedRef<FavoriteSearch[]> = computed(
  () => store.getters["searchHistory/favoriteSearches"]
);

const loginUser: ComputedRef<LoginUser | null> = computed(
  () => store.state.auth.user
);
const loginClient: ComputedRef<Client | null> = computed(
  () => store.state.client.client
);
const isAvailableUserList: ComputedRef<boolean> = computed(() => {
  if (loginUser.value === null) {
    return false;
  }
  return loginUser.value.permission.isAvailableUserList;
});
const isFunnelAvailable: ComputedRef<boolean> = computed(() => {
  if (loginClient.value == null) {
    return false;
  }
  return (
    isAvailableUserList.value && loginClient.value.hasFunnelAnalysisContract
  );
});
const isUserTrendAvailable: ComputedRef<boolean> = computed(() => {
  if (loginUser.value == null) {
    return false;
  }
  return loginUser.value.permission.isAvailableUserTrend;
});
const canUseWebdataFeatures: ComputedRef<boolean> = computed(() => {
  return store.state.app.canUseWebdataFeatures;
});
const isContractApp: ComputedRef<boolean> = computed(() => {
  if (loginClient.value == null) {
    return false;
  }
  return loginClient.value.isContractApp;
});
const isUserTrendAppAvailable: ComputedRef<boolean> = computed(
  () => isUserTrendAvailable.value && isContractApp.value
);
const isUserTrendWebAvailable: ComputedRef<boolean> = computed(
  () => isUserTrendAvailable.value && canUseWebdataFeatures.value
);

function isGuestUser(): boolean {
  if (loginUser.value == null) return false;
  return loginUser.value.isGuest;
}
const behaviorAnalysisCategory: Category = {
  category: t("behaviorAnalysis") as string,
  items: [
    {
      icon: "horizontal-bars",
      text: t("funnelAnalysis") as string,
      url: "/funnel",
      show: isFunnelAvailable.value,
      dataCy: "global_nav_funnel_analysis"
    },
    {
      icon: "branches",
      text: t("journeyAnalysis") as string,
      url: "/journey",
      show: true
    },
    {
      icon: "analysis",
      text: t("accessAnalysis") as string,
      storageKey: "expandable_analysis",
      dataCy: "toggle_analysis_side_bar",
      show: true,
      subitems: [
        {
          icon: "analysis",
          text: t("pageAnalysis") as string,
          routeName: AnalysisType.WebView,
          show: isUserTrendWebAvailable.value,
          dataCy: "global_nav_user-trend_web_view"
        },
        {
          icon: "analysis",
          text: t("landingPageAnalysis") as string,
          routeName: AnalysisType.Landing,
          show: isUserTrendWebAvailable.value,
          dataCy: "global_nav_user-trend_landing"
        },
        {
          icon: "analysis",
          text: t("inflowSourceAnalysis") as string,
          routeName: AnalysisType.Inflow,
          show: isUserTrendWebAvailable.value,
          dataCy: "global_nav_user-trend_inflow"
        },
        {
          icon: "analysis",
          text: t("appPageAnalysis") as string,
          routeName: AnalysisType.AppView,
          show: isUserTrendAppAvailable.value,
          dataCy: "global_nav_user-trend_app_view"
        },
        {
          icon: "analysis",
          text: t("accessOverview") as string,
          url: "/stats",
          show: true
        }
      ]
    }
  ]
};

const favoriteSearchItems: ComputedRef<Item[]> = computed(() =>
  favoriteSearches.value.map((search: FavoriteSearch) => ({
    icon: "pin",
    text: search.label,
    favoriteSearch: search,
    show: true
  }))
);
const myDataCategory: ComputedRef<Category> = computed(() => {
  return {
    category: t("myData") as string,
    items: [
      {
        icon: "heart",
        text: t("bookmarkedUsers") as string,
        url: "/bookmark-users",
        show: true,
        dataCy: "global_nav_favorite_users"
      },
      {
        icon: "pin",
        text: t("savedSearchConditions") as string,
        storageKey: "expandable_pin",
        subitems: favoriteSearchItems.value,
        show: isAvailableUserList.value,
        dataCy: "toggle_favorite_search"
      },
      {
        icon: "history",
        text: t("watchHistory") as string,
        url: "/watch-history",
        show: true,
        dataCy: "global_nav_watch_history"
      },
      {
        icon: "memo",
        text: t("memoUsers") as string,
        url: "/memo-users",
        show: true,
        dataCy: "global_nav_memo_users"
      }
    ]
  };
});

const categories: ComputedRef<Category[]> = computed(() => {
  if (isGuestUser()) {
    return [myDataCategory.value];
  }

  return [behaviorAnalysisCategory, myDataCategory.value];
});

const showMinimized = computed(
  () =>
    isSideBarMinimized.value &&
    !isHovered.value &&
    !isFavoriteSearchBalloonShown.value
);

function setHoverState(hovered: boolean) {
  if (!isSideBarMinimized.value) return;
  isHovered.value = hovered;
}

function goToPage({ routeName, url }: RouteUrl) {
  if (routeName) router.push({ name: routeName });
  else if (url) {
    const origin = window.location.origin;
    window.open(origin + url, "_self");
  }
}

function getIsExpanded(storageKey: StorageKey | undefined): boolean {
  switch (storageKey) {
    case "expandable_analysis":
      return isAnalysisExpanded.value;
    case "expandable_pin":
      return isPinExpanded.value;
    default:
      return false;
  }
}

function onToggleSubItems(storageKey: StorageKey | undefined) {
  if (storageKey === "expandable_analysis") {
    setIsAnalysisExpanded(!isAnalysisExpanded.value);
  } else if (storageKey === "expandable_pin") {
    setIsPinExpanded(!isPinExpanded.value);
  }
}

watch(
  () => route.name,
  () => {
    if (Object.values(AnalysisType).includes(route.name as AnalysisType)) {
      setIsAnalysisExpanded(true);
    }
  }
);

return { __sfc: true,t, route, router, isSideBarMinimized, isAnalysisExpanded, isPinExpanded, setIsAnalysisExpanded, setIsPinExpanded, isHovered, isFavoriteSearchBalloonShown, favoriteSearches, loginUser, loginClient, isAvailableUserList, isFunnelAvailable, isUserTrendAvailable, canUseWebdataFeatures, isContractApp, isUserTrendAppAvailable, isUserTrendWebAvailable, isGuestUser, behaviorAnalysisCategory, favoriteSearchItems, myDataCategory, categories, showMinimized, setHoverState, goToPage, getIsExpanded, onToggleSubItems, V3SideBarGroup, V3SideBarItemNew, V3SideBarItemExpandableNew, isItemActive }
}

})